import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import CustomInputField from "../../components/ui/inputs/CustomInputField";
import CustomDatePicker from "../../components/ui/CustomDatePicker";
import { useDocumentTypes, usePhoneCountryCodes } from "../../hooks/userComplementaryQuery";
import { 
  DEPARTMENTS, 
  BLOOD_TYPES, 
  JOB_POSITIONS, 
  searchInOptions, 
  getPaginatedOptions,
  DEFAULT_DROPDOWN_CONFIG 
} from "../../lib/userProfileData";
import dayjs from "dayjs";
import CalendarIcon from "../../assets/svg/components/calendar.svg";
import CalendarLightIcon from "../../assets/svg/components/calendar_light.svg";
import SortDropdown from '../../components/ui/dropdawn/SortDropdown';
import "../../assets/scss/components/user/PersonalInfoEdit.scss";
import { useGetRoles } from '../../hooks/useReactQuery';

const PersonalInfoEdit = ({
  personalInfo,
  systemInfo,
  onPersonalInfoChange,
  onSystemInfoChange,
  theme
}) => {
  // Hooks para obtener datos complementarios
  const { data: documentTypes, isLoading: isLoadingDocTypes } = useDocumentTypes();
  const { data: countryCodes, isLoading: isLoadingCountryCodes } = usePhoneCountryCodes();
  const { data: rolesData, isLoading: isLoadingRoles } = useGetRoles();
  
  // Estados para manejo de búsquedas en dropdowns
  const [jobPositionSearch, setJobPositionSearch] = useState({
    searchTerm: '',
    currentPage: 1,
    isLoading: false
  });

  // Preparar datos para dropdowns
  const documentTypesData = useMemo(() => {
    if (!documentTypes?.data) return [];
    return documentTypes.data.map(type => ({
      value: type.id,
      label: type.name
    }));
  }, [documentTypes]);

  const countryCodesData = useMemo(() => {
    if (!countryCodes?.data) return [];
    return countryCodes.data.map(country => ({
      value: country.id,
      label: country.country_name,
      country_code: country.country_code
    }));
  }, [countryCodes]);

  const rolesOptions = useMemo(() => {
    if (!rolesData?.data) return [];
    return rolesData.data.map(role => ({
      value: role.id,
      label: role.name
    }));
  }, [rolesData]);

  // Configuración para dropdown de profesiones con búsqueda
  const jobPositionOptions = useMemo(() => {
    const { options } = getPaginatedOptions(
      JOB_POSITIONS,
      jobPositionSearch.currentPage,
      DEFAULT_DROPDOWN_CONFIG.itemsPerPage,
      jobPositionSearch.searchTerm
    );
    return options;
  }, [jobPositionSearch.searchTerm, jobPositionSearch.currentPage]);

  const jobPositionTotalItems = useMemo(() => {
    return searchInOptions(JOB_POSITIONS, jobPositionSearch.searchTerm).length;
  }, [jobPositionSearch.searchTerm]);

  // Handlers para campos básicos
  const handleInputChange = useCallback((field, value) => {
    onPersonalInfoChange(field, value);
  }, [onPersonalInfoChange]);

  const handleSystemChange = useCallback((field, value) => {
    onSystemInfoChange(field, value);
  }, [onSystemInfoChange]);

  const handleDateChange = useCallback((field, date) => {
    const formattedDate = date ? dayjs(date).format('YYYY-MM-DD') : null;
    onPersonalInfoChange(field, formattedDate);
  }, [onPersonalInfoChange]);

  // Handlers específicos para campos con lógica especial
  const handleJobPositionChange = useCallback((value) => {
    handleInputChange('jobPosition', value);
    
    // Si no es "custom", limpiamos el campo personalizado
    if (value !== 'custom') {
      handleInputChange('customJobPosition', '');
    }
  }, [handleInputChange]);

  const handleActiveStatusChange = useCallback((value) => {
    handleSystemChange('activeStatus', value);
    
    // Si se habilita el usuario, limpiamos la razón de deshabilitación
    if (value === true) {
      handleSystemChange('statusReason', '');
    }
  }, [handleSystemChange]);

  // Handlers para búsqueda en job positions
  const handleJobPositionSearch = useCallback((searchTerm) => {
    setJobPositionSearch(prev => ({
      ...prev,
      searchTerm,
      currentPage: 1,
      isLoading: false
    }));
  }, []);

  const handleJobPositionPageChange = useCallback((page) => {
    setJobPositionSearch(prev => ({
      ...prev,
      currentPage: page
    }));
  }, []);

  // Iconos para fecha según el tema
  const dateIconSrc = theme === 'dark' ? CalendarLightIcon : CalendarIcon;

  return (
    <div className={`GM__${theme}-personal-info-edit`}>
      <div className={`GM__${theme}-personal-content`}>
        
        {/* Sección de configuración del sistema */}
        <div className={`GM__${theme}-system-config-section`}>
          <h4 className={`GM__${theme}-subsection-title`}>
            Configuración del Sistema
          </h4>
          <div className={`GM__${theme}-info-grid`}>
            <div className={`GM__${theme}-field-group`}>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="roleId" className={`GM__${theme}-field-label`}>
                  Rol del Usuario *
                </label>
                <SortDropdown
                  value={systemInfo.roleId || ""}
                  onChange={(value) => handleSystemChange('roleId', value)}
                  options={rolesOptions}
                  isLoading={isLoadingRoles}
                  placeholder="Seleccione rol"
                  theme={theme}
                  className={`GM__${theme}-dropdown-field`}
                />
              </div>
              
              <div className={`GM__${theme}-field`}>
                <label className={`GM__${theme}-field-label`}>
                  Estado del Usuario
                </label>
                <div className={`GM__${theme}-switch-container`}>
                  <label className={`GM__${theme}-switch`}>
                    <input
                      type="checkbox"
                      checked={systemInfo.activeStatus}
                      onChange={(e) => handleActiveStatusChange(e.target.checked)}
                    />
                    <span className={`GM__${theme}-slider`}></span>
                  </label>
                  <span className={`GM__${theme}-switch-label`}>
                    {systemInfo.activeStatus ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>
            </div>

            {/* Campo de razón de deshabilitación - Solo visible cuando está inactivo */}
            {!systemInfo.activeStatus && (
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field-full`}>
                  <label htmlFor="statusReason" className={`GM__${theme}-field-label`}>
                    Razón de Deshabilitación *
                  </label>
                  <CustomInputField
                    id="statusReason"
                    value={systemInfo.statusReason}
                    onChange={(e) => handleSystemChange('statusReason', e.target.value)}
                    placeholder="Especifique la razón de deshabilitación"
                    required={!systemInfo.activeStatus}
                    theme={theme}
                    className={`GM__${theme}-input-field`}
                  />
                </div>
              </div>
            )}

            <div className={`GM__${theme}-field-group`}>
              <div className={`GM__${theme}-field`}>
                <label className={`GM__${theme}-field-label`}>
                  Notificaciones Limitadas
                </label>
                <div className={`GM__${theme}-switch-container`}>
                  <label className={`GM__${theme}-switch`}>
                    <input
                      type="checkbox"
                      checked={systemInfo.limitedNotifications}
                      onChange={(e) => handleSystemChange('limitedNotifications', e.target.checked)}
                    />
                    <span className={`GM__${theme}-slider`}></span>
                  </label>
                  <span className={`GM__${theme}-switch-label`}>
                    {systemInfo.limitedNotifications ? 'Recibe todas las notificaciones' : 'Sin notificaciones'}
                  </span>
                </div>
              </div>
              
              <div className={`GM__${theme}-field`}>
                <label className={`GM__${theme}-field-label`}>
                  Flota Propia
                </label>
                <div className={`GM__${theme}-switch-container`}>
                  <label className={`GM__${theme}-switch`}>
                    <input
                      type="checkbox"
                      checked={systemInfo.ownFleet}
                      onChange={(e) => handleSystemChange('ownFleet', e.target.checked)}
                    />
                    <span className={`GM__${theme}-slider`}></span>
                  </label>
                  <span className={`GM__${theme}-switch-label`}>
                    {systemInfo.ownFleet ? 'Flota propia' : 'Flota externa'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sección de información básica */}
        <div className={`GM__${theme}-basic-info-section`}>
          <h4 className={`GM__${theme}-subsection-title`}>
            Información Básica
          </h4>
          <div className={`GM__${theme}-info-grid`}>
            {/* Nombres */}
            <div className={`GM__${theme}-field-group`}>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="firstName" className={`GM__${theme}-field-label`}>
                  Nombre *
                </label>
                <CustomInputField
                  id="firstName"
                  value={personalInfo.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="Ej. Juan Carlos"
                  required
                  theme={theme}
                  className={`GM__${theme}-input-field`}
                />
              </div>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="firstLastName" className={`GM__${theme}-field-label`}>
                  Primer Apellido *
                </label>
                <CustomInputField
                  id="firstLastName"
                  value={personalInfo.firstLastName}
                  onChange={(e) => handleInputChange('firstLastName', e.target.value)}
                  placeholder="Ej. Pérez"
                  required
                  theme={theme}
                  className={`GM__${theme}-input-field`}
                />
              </div>
            </div>

            <div className={`GM__${theme}-field-group`}>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="secondLastName" className={`GM__${theme}-field-label`}>
                  Segundo Apellido
                </label>
                <CustomInputField
                  id="secondLastName"
                  value={personalInfo.secondLastName}
                  onChange={(e) => handleInputChange('secondLastName', e.target.value)}
                  placeholder="Ej. Martínez"
                  theme={theme}
                  className={`GM__${theme}-input-field`}
                />
              </div>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="nationality" className={`GM__${theme}-field-label`}>
                  Nacionalidad
                </label>
                <SortDropdown
                  value={personalInfo.nationality_id || ""}
                  onChange={(value) => handleInputChange('nationality_id', value)}
                  options={countryCodesData}
                  type="nationality"
                  theme={theme}
                  placeholder="Seleccione país"
                  isLoading={isLoadingCountryCodes}
                  className={`GM__${theme}-dropdown-field`}
                />
              </div>
            </div>

            <div className={`GM__${theme}-field-group`}>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="birthDate" className={`GM__${theme}-field-label`}>
                  Fecha de Nacimiento
                </label>
                <CustomDatePicker
                  id="birthDate"
                  value={personalInfo.birthDate ? dayjs(personalInfo.birthDate).toDate() : null}
                  onChange={(date) => handleDateChange('birthDate', date)}
                  icon={<img src={dateIconSrc} alt="calendar" />}
                  theme={theme}
                  className={`GM__${theme}-date-field`}
                />
              </div>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="bloodType" className={`GM__${theme}-field-label`}>
                  Tipo de Sangre
                </label>
                <SortDropdown
                  value={personalInfo.bloodType || ""}
                  onChange={(value) => handleInputChange('bloodType', value)}
                  options={BLOOD_TYPES}
                  placeholder="Seleccione tipo"
                  theme={theme}
                  className={`GM__${theme}-dropdown-field`}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sección de información laboral */}
        <div className={`GM__${theme}-work-info-section`}>
          <h4 className={`GM__${theme}-subsection-title`}>
            Información Laboral
          </h4>
          <div className={`GM__${theme}-info-grid`}>
            <div className={`GM__${theme}-field-group`}>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="department" className={`GM__${theme}-field-label`}>
                  Departamento
                </label>
                <SortDropdown
                  value={personalInfo.department || ""}
                  onChange={(value) => handleInputChange('department', value)}
                  options={DEPARTMENTS}
                  placeholder="Seleccione departamento"
                  theme={theme}
                  className={`GM__${theme}-dropdown-field`}
                />
              </div>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="jobPosition" className={`GM__${theme}-field-label`}>
                  Cargo/Posición
                </label>
                <SortDropdown
                  value={personalInfo.jobPosition || ""}
                  onChange={handleJobPositionChange}
                  options={jobPositionOptions}
                  placeholder="Seleccione cargo"
                  theme={theme}
                  enableSearch={true}
                  enablePagination={true}
                  onSearch={handleJobPositionSearch}
                  searchPlaceholder="Buscar cargo..."
                  itemsPerPage={DEFAULT_DROPDOWN_CONFIG.itemsPerPage}
                  totalItems={jobPositionTotalItems}
                  currentPage={jobPositionSearch.currentPage}
                  onPageChange={handleJobPositionPageChange}
                  searchTerm={jobPositionSearch.searchTerm}
                  isLoading={jobPositionSearch.isLoading}
                  className={`GM__${theme}-dropdown-field`}
                />
              </div>
            </div>

            {/* Campo personalizado para cargo - Solo visible cuando se selecciona "Otro" */}
            {personalInfo.jobPosition === 'custom' && (
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field-full`}>
                  <label htmlFor="customJobPosition" className={`GM__${theme}-field-label`}>
                    Especificar Cargo *
                  </label>
                  <CustomInputField
                    id="customJobPosition"
                    value={personalInfo.customJobPosition}
                    onChange={(e) => handleInputChange('customJobPosition', e.target.value)}
                    placeholder="Escriba el cargo específico"
                    required={personalInfo.jobPosition === 'custom'}
                    theme={theme}
                    className={`GM__${theme}-input-field`}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sección de documentación */}
        <div className={`GM__${theme}-document-info-section`}>
          <h4 className={`GM__${theme}-subsection-title`}>
            Información de Documentación
          </h4>
          <div className={`GM__${theme}-info-grid`}>
            <div className={`GM__${theme}-field-group`}>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="documentTypeId" className={`GM__${theme}-field-label`}>
                  Tipo de Documento *
                </label>
                <SortDropdown
                  value={personalInfo.documentTypeId || ""}
                  onChange={(value) => handleInputChange('documentTypeId', value)}
                  options={documentTypesData}
                  isLoading={isLoadingDocTypes}
                  placeholder="Seleccione tipo"
                  theme={theme}
                  className={`GM__${theme}-dropdown-field`}
                />
              </div>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="documentNumber" className={`GM__${theme}-field-label`}>
                  Número de Documento *
                </label>
                <CustomInputField
                  id="documentNumber"
                  value={personalInfo.documentNumber}
                  onChange={(e) => handleInputChange('documentNumber', e.target.value)}
                  placeholder="Ej. 12345678"
                  required
                  theme={theme}
                  className={`GM__${theme}-input-field`}
                />
              </div>
            </div>

            <div className={`GM__${theme}-field-group`}>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="documentExpDate" className={`GM__${theme}-field-label`}>
                  Fecha de Expedición
                </label>
                <CustomDatePicker
                  id="documentExpDate"
                  value={personalInfo.documentExpDate ? dayjs(personalInfo.documentExpDate).toDate() : null}
                  onChange={(date) => handleDateChange('documentExpDate', date)}
                  icon={<img src={dateIconSrc} alt="calendar" />}
                  theme={theme}
                  className={`GM__${theme}-date-field`}
                />
              </div>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="documentExpeditionPlace" className={`GM__${theme}-field-label`}>
                  Lugar de Expedición
                </label>
                <CustomInputField
                  id="documentExpeditionPlace"
                  value={personalInfo.documentExpeditionPlace}
                  onChange={(e) => handleInputChange('documentExpeditionPlace', e.target.value)}
                  placeholder="Ej. Bogotá"
                  theme={theme}
                  className={`GM__${theme}-input-field`}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

PersonalInfoEdit.propTypes = {
  personalInfo: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    firstLastName: PropTypes.string.isRequired,
    secondLastName: PropTypes.string,
    documentNumber: PropTypes.string,
    documentExpDate: PropTypes.string,
    documentTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    documentExpeditionPlace: PropTypes.string,
    nationality_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    birthDate: PropTypes.string,
    // Nuevos campos
    department: PropTypes.string,
    bloodType: PropTypes.string,
    jobPosition: PropTypes.string,
    customJobPosition: PropTypes.string,
  }).isRequired,
  systemInfo: PropTypes.shape({
    roleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    activeStatus: PropTypes.bool,
    statusReason: PropTypes.string,
    limitedNotifications: PropTypes.bool,
    ownFleet: PropTypes.bool,
  }).isRequired,
  onPersonalInfoChange: PropTypes.func.isRequired,
  onSystemInfoChange: PropTypes.func.isRequired,
  theme: PropTypes.oneOf(['dark', 'light']).isRequired,
};

export default PersonalInfoEdit;